import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Server, Clock, RefreshCw, CheckCircle, XCircle, AlertTriangle, Building2, Users, FileText, HardDrive } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { token, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.is_super_admin) {
      toast.error('Accesso non autorizzato');
      navigate('/dashboard');
      return;
    }
    fetchHealth();
  }, [token, user, navigate]);

  const fetchHealth = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setHealth(data);
      } else {
        toast.error('Errore nel caricamento dello stato');
      }
    } catch (error) {
      console.error('Error fetching health:', error);
      toast.error('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await fetchHealth();
    setRefreshing(false);
    toast.success('Stato aggiornato');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'ACTIVE': { color: 'bg-green-100 text-green-800', label: 'Attivo' },
      'TRIAL': { color: 'bg-yellow-100 text-yellow-800', label: 'Trial' },
      'EXPIRED': { color: 'bg-red-100 text-red-800', label: 'Scaduto' },
      'SUSPENDED': { color: 'bg-gray-100 text-gray-800', label: 'Sospeso' }
    };
    const config = statusMap[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getBooleanBadge = (value, trueLabel = 'Sì', falseLabel = 'No') => {
    return value 
      ? <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />{trueLabel}</Badge>
      : <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />{falseLabel}</Badge>;
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return 'text-red-600';
    if (percent >= 80) return 'text-orange-500';
    return 'text-green-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!health) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Errore</AlertTitle>
        <AlertDescription>Impossibile caricare lo stato del sistema</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Stato Sistema"
        subtitle="Diagnostica della piattaforma"
        actions={
          <Button variant="outline" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        }
      />

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Plan Status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Piano</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              {getStatusBadge(health.workspace?.plan_status)}
            </div>
          </CardContent>
        </Card>

        {/* Trial Ends */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Trial Scade</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium">
                {formatDate(health.workspace?.trial_ends_at)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Scheduler */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Scheduler</CardTitle>
          </CardHeader>
          <CardContent>
            {getBooleanBadge(health.services?.scheduler_running, 'Attivo', 'Fermo')}
          </CardContent>
        </Card>

        {/* SMTP */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">SMTP</CardTitle>
          </CardHeader>
          <CardContent>
            {getBooleanBadge(health.services?.smtp_enabled, 'Abilitato', 'Disabilitato')}
          </CardContent>
        </Card>
      </div>

      {/* Usage Percentages */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Utilizzo Risorse (%)
          </CardTitle>
          <CardDescription>Percentuale di utilizzo per ogni risorsa</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {/* Condominiums */}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Building2 className="h-6 w-6 mx-auto mb-2 text-gray-400" />
              <div className={`text-2xl font-bold ${getUsageColor(health.workspace?.usage_percent?.condominiums || 0)}`}>
                {health.workspace?.usage_percent?.condominiums || 0}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Condomini</div>
            </div>

            {/* Units */}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Building2 className="h-6 w-6 mx-auto mb-2 text-gray-400" />
              <div className={`text-2xl font-bold ${getUsageColor(health.workspace?.usage_percent?.units || 0)}`}>
                {health.workspace?.usage_percent?.units || 0}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Unità</div>
            </div>

            {/* Users */}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Users className="h-6 w-6 mx-auto mb-2 text-gray-400" />
              <div className={`text-2xl font-bold ${getUsageColor(health.workspace?.usage_percent?.users || 0)}`}>
                {health.workspace?.usage_percent?.users || 0}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Utenti</div>
            </div>

            {/* Documents */}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <FileText className="h-6 w-6 mx-auto mb-2 text-gray-400" />
              <div className={`text-2xl font-bold ${getUsageColor(health.workspace?.usage_percent?.documents || 0)}`}>
                {health.workspace?.usage_percent?.documents || 0}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Documenti</div>
            </div>

            {/* Storage */}
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <HardDrive className="h-6 w-6 mx-auto mb-2 text-gray-400" />
              <div className={`text-2xl font-bold ${getUsageColor(health.workspace?.usage_percent?.storage || 0)}`}>
                {health.workspace?.usage_percent?.storage || 0}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Storage</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Statistiche Sistema</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600">
                {health.system_stats?.total_workspaces || 0}
              </div>
              <div className="text-sm text-gray-500">Workspace Totali</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600">
                {health.system_stats?.total_users || 0}
              </div>
              <div className="text-sm text-gray-500">Utenti Totali</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600">
                {health.system_stats?.total_condominiums || 0}
              </div>
              <div className="text-sm text-gray-500">Condomini Totali</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timestamp */}
      <div className="text-center text-xs text-gray-400">
        Ultimo aggiornamento: {formatDate(health.timestamp)}
      </div>
    </div>
  );
}
